import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, ExternalLink, AlertTriangle, RefreshCw, Smartphone, Monitor, Sparkles, Pencil, Trash2, Plus, Save, Lightbulb } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FINAL = ['rascunho_ia', 'em_revisao', 'revisado', 'erro'];

const scoreColor = (s) => {
  if (s === null || s === undefined) return 'text-slate-400 border-slate-300';
  const v = Math.round(s * 100);
  if (v >= 90) return 'text-green-600 border-green-500';
  if (v >= 50) return 'text-amber-500 border-amber-400';
  return 'text-red-600 border-red-500';
};
const CAT_LABELS = { performance: 'Performance', accessibility: 'Acessibilidade', 'best-practices': 'Boas práticas', seo: 'SEO' };

const SEV = {
  baixa: { label: 'Baixa', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  media: { label: 'Média', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  alta: { label: 'Alta', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
  critica: { label: 'Crítica', cls: 'bg-red-100 text-red-700 border-red-300' },
};
const SEV_ORDER = { critica: 0, alta: 1, media: 2, baixa: 3 };
const NIELSEN = [
  'Visibilidade do status do sistema',
  'Correspondência entre o sistema e o mundo real',
  'Controle e liberdade do usuário',
  'Consistência e padrões',
  'Prevenção de erros',
  'Reconhecimento em vez de memorização',
  'Flexibilidade e eficiência de uso',
  'Design estético e minimalista',
  'Ajudar a reconhecer, diagnosticar e recuperar de erros',
  'Ajuda e documentação',
];
const REVIEW_LABELS = { rascunho_ia: 'Rascunho da IA', em_revisao: 'Em revisão', revisado: 'Revisado' };

// Core Web Vitals thresholds (good / needs-improvement upper bounds)
const cwvStatus = (id, numeric) => {
  if (numeric === null || numeric === undefined) return null;
  const t = { 'largest-contentful-paint': [2500, 4000], 'cumulative-layout-shift': [0.1, 0.25], 'total-blocking-time': [200, 500] }[id];
  if (!t) return null;
  if (numeric <= t[0]) return 'text-green-600';
  if (numeric <= t[1]) return 'text-amber-500';
  return 'text-red-600';
};

const ScoreCircle = ({ label, score }) => {
  const v = score === null || score === undefined ? '—' : Math.round(score * 100);
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`score-${label}`}>
      <div className={`h-16 w-16 rounded-full border-4 flex items-center justify-center font-bold text-lg ${scoreColor(score)}`}>{v}</div>
      <span className="text-xs text-muted-foreground text-center">{CAT_LABELS[label] || label}</span>
    </div>
  );
};

const DeviceReport = ({ data }) => {
  if (!data) return <p className="text-sm text-muted-foreground">Sem dados.</p>;
  if (data.error) return <div className="flex items-center gap-2 text-sm text-red-600"><AlertTriangle className="h-4 w-4" />{data.error}</div>;
  const cats = data.categories || {};
  const metrics = data.metrics || {};
  const field = data.loadingExperience?.metrics ? data.loadingExperience : data.originLoadingExperience;
  const fieldReal = !!data.loadingExperience?.metrics;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-6 justify-center">
        {['performance', 'accessibility', 'best-practices', 'seo'].map((k) => <ScoreCircle key={k} label={k} score={cats[k]} />)}
      </div>
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h4 className="font-semibold text-sm">Core Web Vitals</h4>
          <Badge variant={fieldReal ? 'default' : 'secondary'} className="text-[10px]">{fieldReal ? 'Dados reais de usuários' : 'Teste de laboratório'}</Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {[['largest-contentful-paint', 'LCP'], ['cumulative-layout-shift', 'CLS'], ['total-blocking-time', 'TBT'], ['first-contentful-paint', 'FCP'], ['speed-index', 'Speed Index']].map(([id, short]) => {
            const m = metrics[id];
            const cls = cwvStatus(id, m?.numericValue) || 'text-foreground';
            return (
              <div key={id} className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">{short}</p>
                <p className={`font-mono font-bold ${cls}`}>{m?.displayValue || '—'}</p>
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-sm mb-2">Principais oportunidades</h4>
        {(data.opportunities || []).length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma oportunidade relevante.</p> : (
          <ul className="space-y-2">
            {data.opportunities.map((o) => (
              <li key={o.id} className="rounded-lg border p-3">
                <div className="flex justify-between gap-3">
                  <span className="text-sm font-medium">{o.title}</span>
                  {o.displayValue && <span className="text-xs font-mono text-amber-600 whitespace-nowrap">{o.displayValue}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const emptyFinding = () => ({ id: `new-${Math.random().toString(36).slice(2)}`, heuristica: NIELSEN[0], pagina: 'geral', o_que_observamos: '', severidade: 'media', recomendacao: '' });

const UsabilityTab = ({ diag, reload }) => {
  const id = diag.id;
  const u = diag.usability;
  const pagesReady = ((diag.pagespeed || {}).results || []).some((r) => !r.error);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null); // {summary:{texto,top_recomendacoes}, findings:[]}

  const generate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/diagnostics/${id}/usability/generate`);
      toast.success('Gerando análise de usabilidade…');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao iniciar a análise.');
    } finally { setBusy(false); }
  };

  const startEdit = () => {
    setDraft({
      summary: { texto: u.summary?.texto || '', top_recomendacoes: [...(u.summary?.top_recomendacoes || [])] },
      findings: (u.findings || []).map((f) => ({ ...f })),
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const cleanFindings = draft.findings.filter((f) => (f.o_que_observamos || '').trim() || (f.recomendacao || '').trim());
      const res = await axios.put(`${API_URL}/api/diagnostics/${id}/usability`, {
        summary: { texto: draft.summary.texto, top_recomendacoes: draft.summary.top_recomendacoes.filter((r) => r.trim()) },
        findings: cleanFindings.map(({ id: fid, ...rest }) => (String(fid).startsWith('new-') ? rest : { id: fid, ...rest })),
      });
      toast.success('Análise atualizada.');
      setEditing(false); setDraft(null);
      await reload();
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao salvar.');
    } finally { setBusy(false); }
  };

  const setReview = async (rs) => {
    setBusy(true);
    try {
      await axios.put(`${API_URL}/api/diagnostics/${id}/usability`, { review_status: rs });
      toast.success(`Marcado como "${REVIEW_LABELS[rs]}".`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao atualizar status.');
    } finally { setBusy(false); }
  };

  // Empty / first run
  if (!u) {
    return (
      <Card><CardContent className="py-16 text-center space-y-4" data-testid="usability-empty">
        <Sparkles className="h-8 w-8 mx-auto text-primary" />
        <p className="text-muted-foreground max-w-md mx-auto">Gere uma pré-análise de usabilidade por IA cruzando as heurísticas de Nielsen com os dados de desempenho (PageSpeed) e as capturas de tela das páginas.</p>
        <Button onClick={generate} disabled={!pagesReady || busy} data-testid="generate-usability-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}Gerar análise de usabilidade
        </Button>
        {!pagesReady && <p className="text-xs text-muted-foreground">Aguarde o diagnóstico de desempenho concluir.</p>}
      </CardContent></Card>
    );
  }

  if (u.status === 'processando') {
    return (
      <Card><CardContent className="py-16 flex flex-col items-center gap-3" data-testid="usability-processing">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="text-muted-foreground">Gerando análise de usabilidade com IA…</span>
      </CardContent></Card>
    );
  }

  if (u.status === 'erro') {
    return (
      <Card className="border-red-300"><CardContent className="py-8 flex flex-col items-center gap-3" data-testid="usability-error">
        <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />{u.error || 'Falha ao gerar a análise.'}</div>
        <Button onClick={generate} disabled={busy} data-testid="usability-retry-btn"><RefreshCw className="h-4 w-4 mr-2" />Tentar novamente</Button>
      </CardContent></Card>
    );
  }

  const findings = editing ? draft.findings : (u.findings || []);
  const sortedFindings = [...findings].sort((a, b) => (SEV_ORDER[a.severidade] ?? 2) - (SEV_ORDER[b.severidade] ?? 2));
  const summary = editing ? draft.summary : (u.summary || { texto: '', top_recomendacoes: [] });

  const upd = (fn) => setDraft((d) => { const n = { ...d, findings: [...d.findings] }; fn(n); return n; });

  return (
    <div className="space-y-5" data-testid="usability-content">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="usability-review-badge">{REVIEW_LABELS[u.review_status] || 'Rascunho da IA'}</Badge>
          {!editing && (
            <Select value={u.review_status || 'rascunho_ia'} onValueChange={setReview} disabled={busy}>
              <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="review-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REVIEW_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="usability-edit-btn"><Pencil className="h-4 w-4 mr-1" />Editar</Button>
              <Button variant="outline" size="sm" onClick={generate} disabled={busy || u.status === 'processando'} data-testid="usability-regenerate-btn"><RefreshCw className="h-4 w-4 mr-1" />Gerar novamente</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setDraft(null); }} data-testid="usability-cancel-btn">Cancelar</Button>
              <Button size="sm" onClick={saveEdit} disabled={busy} data-testid="usability-save-btn">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Salvar</Button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <Card data-testid="usability-summary">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Lightbulb className="h-4 w-4 text-amber-500" />Resumo da usabilidade</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {editing ? (
            <Textarea value={summary.texto} onChange={(e) => setDraft((d) => ({ ...d, summary: { ...d.summary, texto: e.target.value } }))} rows={4} data-testid="summary-texto-input" />
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-line">{summary.texto || 'Sem resumo.'}</p>
          )}
          <div>
            <p className="text-sm font-semibold mb-2">Recomendações prioritárias</p>
            {editing ? (
              <div className="space-y-2">
                {summary.top_recomendacoes.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={r} onChange={(e) => setDraft((d) => { const recs = [...d.summary.top_recomendacoes]; recs[i] = e.target.value; return { ...d, summary: { ...d.summary, top_recomendacoes: recs } }; })} data-testid={`rec-input-${i}`} />
                    <Button variant="ghost" size="icon" onClick={() => setDraft((d) => ({ ...d, summary: { ...d.summary, top_recomendacoes: d.summary.top_recomendacoes.filter((_, j) => j !== i) } }))}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setDraft((d) => ({ ...d, summary: { ...d.summary, top_recomendacoes: [...d.summary.top_recomendacoes, ''] } }))} data-testid="add-rec-btn"><Plus className="h-4 w-4 mr-1" />Adicionar recomendação</Button>
              </div>
            ) : (
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                {(summary.top_recomendacoes || []).length === 0 ? <li className="list-none">—</li> : summary.top_recomendacoes.map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Findings */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Achados por heurística ({sortedFindings.length})</h3>
          {editing && <Button variant="outline" size="sm" onClick={() => upd((n) => n.findings.push(emptyFinding()))} data-testid="add-finding-btn"><Plus className="h-4 w-4 mr-1" />Adicionar achado</Button>}
        </div>
        {sortedFindings.length === 0 && <p className="text-sm text-muted-foreground">Nenhum achado.</p>}
        {sortedFindings.map((f) => {
          const idx = findings.indexOf(f);
          return (
            <Card key={f.id} data-testid={`finding-${f.id}`}>
              <CardContent className="py-4 space-y-2">
                {editing ? (
                  <div className="space-y-2">
                    <div className="grid sm:grid-cols-2 gap-2">
                      <Select value={f.heuristica} onValueChange={(v) => upd((n) => { n.findings[idx] = { ...n.findings[idx], heuristica: v }; })}>
                        <SelectTrigger className="text-xs" data-testid={`finding-heur-${f.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{NIELSEN.map((h) => <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={f.severidade} onValueChange={(v) => upd((n) => { n.findings[idx] = { ...n.findings[idx], severidade: v }; })}>
                        <SelectTrigger className="text-xs" data-testid={`finding-sev-${f.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(SEV).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input value={f.pagina} onChange={(e) => upd((n) => { n.findings[idx] = { ...n.findings[idx], pagina: e.target.value }; })} placeholder="URL ou 'geral'" className="text-xs" data-testid={`finding-page-${f.id}`} />
                    <Textarea value={f.o_que_observamos} onChange={(e) => upd((n) => { n.findings[idx] = { ...n.findings[idx], o_que_observamos: e.target.value }; })} rows={2} placeholder="O que observamos" data-testid={`finding-obs-${f.id}`} />
                    <Textarea value={f.recomendacao} onChange={(e) => upd((n) => { n.findings[idx] = { ...n.findings[idx], recomendacao: e.target.value }; })} rows={2} placeholder="Recomendação" data-testid={`finding-rec-${f.id}`} />
                    <div className="flex justify-end"><Button variant="ghost" size="sm" onClick={() => upd((n) => { n.findings = n.findings.filter((x) => x.id !== f.id); })} data-testid={`finding-delete-${f.id}`}><Trash2 className="h-4 w-4 mr-1 text-red-500" />Remover</Button></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <span className="text-sm font-medium">{f.heuristica}</span>
                      <Badge variant="outline" className={SEV[f.severidade]?.cls}>{SEV[f.severidade]?.label || f.severidade}</Badge>
                    </div>
                    {f.pagina && f.pagina !== 'geral' && <p className="text-xs text-muted-foreground truncate">{f.pagina.replace(/^https?:\/\//, '')}</p>}
                    <p className="text-sm">{f.o_que_observamos}</p>
                    <p className="text-sm text-primary"><b>Recomendação:</b> {f.recomendacao}</p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export const DiagnosticDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState({});  // url -> 'mobile'|'desktop'
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/diagnostics/${id}`);
      setDiag(res.data);
      return res.data;
    } catch (e) { console.error(e); return null; } finally { setLoading(false); }
  }, [id]);

  const startPoll = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const d = await load();
      const usabilityProcessing = d && d.usability && d.usability.status === 'processando';
      if (d && FINAL.includes(d.status) && !usabilityProcessing && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 5000);
  }, [load]);

  const reloadAndPoll = useCallback(async () => {
    const d = await load();
    if (d && d.usability && d.usability.status === 'processando') startPoll();
    return d;
  }, [load, startPoll]);

  useEffect(() => {
    load();
    startPoll();
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [load, startPoll]);

  const retry = async () => { await axios.post(`${API_URL}/api/diagnostics/${id}/retry`); setLoading(true); load(); };
  const regenSummary = async () => { await axios.post(`${API_URL}/api/diagnostics/${id}/regenerate-summary`); };

  if (loading || !diag) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const ps = diag.pagespeed || {};
  const results = ps.results || [];
  const progress = ps.progress || { done: 0, total: (diag.pages || []).length };
  const totalUnits = progress.total * 2; // page x device
  const doneUnits = progress.done * 2;

  return (
    <div className="space-y-6" data-testid="diagnostic-detail">
      <button onClick={() => navigate('/diagnostics')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Voltar</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Diagnóstico de {new Date(diag.created_at).toLocaleDateString('pt-BR')}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {(diag.pages || []).map((p) => <Badge key={p.url} variant="outline" className="max-w-xs truncate">{p.page_type}: {p.url.replace(/^https?:\/\//, '')}</Badge>)}
          </div>
        </div>
        <Badge variant={diag.status === 'erro' ? 'destructive' : 'secondary'}>{diag.status}</Badge>
      </div>

      {diag.status === 'processando' && (
        <Card><CardContent className="py-6 flex items-center gap-3" data-testid="processing-banner">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span>Analisando {doneUnits} de {totalUnits} (página × dispositivo)…</span>
        </CardContent></Card>
      )}

      {diag.status === 'erro' && (
        <Card className="border-red-300"><CardContent className="py-6 flex items-center justify-between gap-3" data-testid="error-banner">
          <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" />{diag.error_message || 'Falha no processamento.'}</div>
          <Button onClick={retry} data-testid="retry-btn"><RefreshCw className="h-4 w-4 mr-2" /> Tentar novamente</Button>
        </CardContent></Card>
      )}

      {diag.status !== 'erro' && (
        <Tabs defaultValue="desempenho">
          <TabsList>
            <TabsTrigger value="desempenho" data-testid="tab-desempenho">Desempenho</TabsTrigger>
            <TabsTrigger value="usabilidade" data-testid="tab-usabilidade">Usabilidade</TabsTrigger>
          </TabsList>

          <TabsContent value="desempenho" className="space-y-5 pt-4">
            {/* AI summary */}
            {ps.summary?.items?.length > 0 && (
              <Card data-testid="ai-summary"><CardHeader><CardTitle className="text-base">Resumo em linguagem simples</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {ps.summary.items.map((it, i) => (
                    <div key={i} className="rounded-lg border p-3">
                      <p className="font-medium text-sm">{it.o_que}</p>
                      <p className="text-sm text-muted-foreground mt-1"><b>Por que importa:</b> {it.por_que}</p>
                      <p className="text-sm text-primary mt-1"><b>O que fazer:</b> {it.o_que_fazer}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {!ps.summary && ps.summary_error && (
              <Card><CardContent className="py-4 flex items-center justify-between gap-3" data-testid="summary-unavailable">
                <span className="text-sm text-muted-foreground">Resumo indisponível.</span>
                <Button variant="outline" size="sm" onClick={regenSummary}>Gerar novamente</Button>
              </CardContent></Card>
            )}

            {results.map((r) => {
              const dev = device[r.url] || 'mobile';
              return (
                <Card key={r.url} data-testid={`page-report-${r.url}`}>
                  <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base truncate">{r.url.replace(/^https?:\/\//, '')}</CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-md border overflow-hidden">
                        <button className={`px-2 py-1 text-xs flex items-center gap-1 ${dev === 'mobile' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => setDevice((p) => ({ ...p, [r.url]: 'mobile' }))} data-testid={`device-mobile-${r.url}`}><Smartphone className="h-3 w-3" />Mobile</button>
                        <button className={`px-2 py-1 text-xs flex items-center gap-1 ${dev === 'desktop' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => setDevice((p) => ({ ...p, [r.url]: 'desktop' }))} data-testid={`device-desktop-${r.url}`}><Monitor className="h-3 w-3" />Desktop</button>
                      </div>
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-primary"><ExternalLink className="h-3 w-3" />Abrir página</a>
                      <a href={`https://pagespeed.web.dev/analysis?url=${encodeURIComponent(r.url)}`} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1 text-primary"><ExternalLink className="h-3 w-3" />Ver no PageSpeed</a>
                    </div>
                  </CardHeader>
                  <CardContent><DeviceReport data={r[dev]} /></CardContent>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="usabilidade" className="pt-4">
            <UsabilityTab diag={diag} reload={reloadAndPoll} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
